import React, { memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SIDEBAR_ACTIONS } from "../../constants/actionsConstants";
import SIDEBARS from "../../constants/sidebars";

/**
 * Componente Organism para renderizar un sidebar individual (izquierda, derecha o modal central).
 *
 * Implementa renderizado condicional y memoización para máxima performance.
 *
 * @component ItemSidebar
 * @category Component - Organism
 * @description
 * - Renderiza el componente correspondiente según la clave y props recibidas.
 * - Soporta posiciones 'left', 'right' y 'center' para layout flexible.
 * - Utiliza TailwindCSS y arquitectura Atomic Design.
 *
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Indica si el sidebar está abierto
 * @param {SidebarItem} props.children - Objeto con datos del sidebar a renderizar
 * @param {string} [props.position="right"] - Posición del sidebar ('left', 'right', 'center')
 *
 * @returns {JSX.Element|null} Elemento JSX del sidebar renderizado o null si está cerrado
 *
 * @example
 * <ItemSidebar isOpen={true} position="left">{sidebarItem}</ItemSidebar>
 *
 * @see SIDEBARS Diccionario de componentes disponibles
 */
const ItemSidebar = memo(({
  isOpen,
  children,
  position = "right",
  className = ''
}) => {
  const dispatch = useDispatch();

  const handleOverlayClick = () => {
    const shouldClose = children?.closeOnOverlayClick !== false;
    if (shouldClose && children?.componentKey) {
      dispatch({
        type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
        payload: { componentKey: children.componentKey }
      });
    }
  };

  const renderComponent = () => {
    if (!children?.componentKey) return null;
    const Component = SIDEBARS[children.componentKey];

    if (!Component) {
      return null;
    }
    return React.createElement(Component, children?.props || {});
  };

  // Si el className contiene clases de ancho, usar solo ese valor
  const hasCustomWidth = typeof className === 'string' && /tw-w-|tw-max-w-|tw-min-w-/.test(className);
  const defaultClass = hasCustomWidth ? className : (className || 'tw-w-[40vw] tw-max-w-[600px]');
  const isCenter = position === "center";
  const sidebarBase = `tw-fixed tw-top-0 tw-h-screen tw-min-w-[328px] tw-bg-white tw-shadow-lg tw-z-[10003] tw-flex tw-flex-col ${defaultClass}`;

  const leftClasses = `tw-left-0 ${sidebarBase}`;
  const rightClasses = `tw-right-0 ${sidebarBase}`;

  return (
    <div className="tw-wrapper">
      {isOpen && (
        <>
          <div
            className="tw-fixed tw-inset-0 tw-bg-black/50 tw-backdrop-blur-sm tw-z-[10002]"
            onClick={handleOverlayClick}
            role="presentation"
          />
          {isCenter ? (
            <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-[10003] pointer-events-none">
              <div
                className="
                  pointer-events-auto
                  tw-bg-white tw-rounded-xl tw-shadow-lg
                  tw-max-w-[90vw] tw-w-auto
                  tw-max-h-[90vh]
                  tw-overflow-y-auto
                  tw-p-6
                "
              >
                <div className="tw-h-auto">
                  {renderComponent()}
                </div>
              </div>
            </div>
          ) : (
            <div className={position === "left" ? leftClasses : rightClasses}>
              <div className="tw-flex-1 tw-h-full">
                {renderComponent()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

const SidebarContainerMC = memo(() => {
  const dispatch = useDispatch();
  const sidebarItems = useSelector((state) => state.sidebarReducer.contentComponents || []);

  useEffect(() => {
    window.openSidebarMC = (componentKey, props = {}) => {
      dispatch({
        type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
        payload: { componentKey, props }
      });
    };

    return () => {
      delete window.openSidebarMC;
    };
  }, [dispatch]);

  const openSidebars = React.useMemo(
    () => sidebarItems.filter((sidebarItem) => sidebarItem?.isOpen),
    [sidebarItems]
  );

  return openSidebars.map((sidebarItem) => (
    <ItemSidebar
      key={sidebarItem.id || sidebarItem.sidebarId || sidebarItem.componentKey}
      isOpen={sidebarItem.isOpen}
      position={sidebarItem.position ?? "right"}
      className={sidebarItem.className || ''}
    >
      {sidebarItem}
    </ItemSidebar>
  ));
});

export default SidebarContainerMC;
